import React, { useMemo, useState } from 'react';
import { ButtonColor } from '../../../components/uikit/buttons/Button.tsx';
import styles from './UserManagement.module.scss';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { CustomModal } from '../../../components/uikit/modal/CustomModal.tsx';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import clsx from 'clsx';
import { UserRole } from '../../../services/api/api-client.types.ts';
import { Autocomplete, TextField } from '@mui/material';
import ArrowDown from 'assets/icons/arrow-down-selector.svg?react';
import { useWatch } from 'react-hook-form';
import { SelectOption } from './SelectOption.tsx';
import CloseIcon from 'assets/icons/close-button-small.svg?react';
import _ from 'lodash';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { SemiAdminClient, UserClient } from 'services/api/api-client.ts';
import { errorToString } from '../../../helpers/error-helpers.ts';
import { SelectedUser } from './UserManagement.tsx';
import { getCountries } from '../../../helpers/countries-helper.ts';

type Form = {
  name: string;
  email: string;
  selectedCountry: {
    label: string;
    value: string;
  };
  roles: UserRole[];
};

type OwnProps = {
  closeModal: () => void;
  refetch: () => void;
  showUserActionsTooltip(info: string): void;
  selectedUser: SelectedUser | null;
  userRoles: UserRole[];
};

export const UserSettingsModal = (props: OwnProps) => {
  const i18n = useScopedTranslation('Page.UserManagement');

  const [rolesInputValue, setRolesInputValue] = useState('');
  const [countryInputValue, setCountryInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRemindModalActive, setIsRemindModalActive] = useState(false);

  const {
    closeModal,
    showUserActionsTooltip,
    refetch,
    selectedUser,
    userRoles,
  } = props;

  const isEditMode = !!selectedUser;
  const isAbasUser = !!selectedUser?.customerNumber;

  const isAdmin = userRoles.includes(UserRole.Admin);
  const isClevelandAccessIncluded = userRoles.includes(
    UserRole.ClevelandAccess,
  );

  const allAvailableRoles = [
    ...(isClevelandAccessIncluded || isAdmin ? [UserRole.ClevelandAccess] : []),
    UserRole.SoftwareAccess,
    UserRole.FirmwareAccess,
    ...(isAdmin ? [UserRole.SemiAdmin] : []),
  ];

  const language = i18n.i18n.language;

  const countryOptions = useMemo(
    () => getCountries(language.split('-')[0]),
    [language],
  );

  const countryOption = countryOptions.find(
    (o) => o.value === selectedUser?.countryCode,
  ) ?? {
    value: '',
    label: '',
  };

  const form = useAdvancedForm<Form>(
    async () => {
      try {
        setIsLoading(true);

        if (isEditMode) {
          const oldRoles = selectedUser?.claimRoles.filter(
            (userRole) => !allAvailableRoles.includes(userRole),
          );

          await UserClient.updateUserInformation(selectedUser?.id, {
            claims: [...oldRoles, ...selectedRoles],
            countryCode: selectedCountry.value,
          });
        } else {
          await SemiAdminClient.createUser({
            email,
            roles: selectedRoles,
            realName: name,
            countryCode: selectedCountry.value,
          });
        }

        closeModal();
        if (!isEditMode) {
          showUserActionsTooltip(i18n.t('user_successfully_added'));
        }
        refetch();
      } catch (e) {
        form.setError('email', {
          message: errorToString(e),
        });
      } finally {
        setIsLoading(false);
      }
    },
    {
      defaultValues: {
        name: selectedUser?.name ?? '',
        email: selectedUser?.email ?? '',
        selectedCountry: countryOption,
        roles:
          selectedUser?.claimRoles?.filter((role) =>
            allAvailableRoles.includes(role),
          ) ?? [],
      },
    },
  );

  const [selectedRoles, name, email, selectedCountry] = useWatch({
    control: form.control,
    name: ['roles', 'name', 'email', 'selectedCountry'],
  });

  const isCountryReadOnly = isEditMode && isAbasUser;
  const isCountryRequired = !isCountryReadOnly;

  const handleSubmit = async () => {
    const result = await form.trigger();

    if (isCountryRequired && !selectedCountry?.value) {
      form.setError('selectedCountry', {
        type: 'required',
        message: i18n.t('required_error'),
      });
      return;
    }

    if (!result) return;

    void form.handleSubmitDefault();
  };

  const removeSelectedRole = (role: UserRole) => {
    const copy = _.cloneDeep(selectedRoles);
    const updated = copy.filter((r) => r !== role);

    form.setValue('roles', updated, {
      shouldDirty: true,
    });
  };

  const isSubmitButtonDisabled =
    !name.trim() || !email.trim() || (!selectedCountry.value && !isEditMode);

  return (
    <CustomModal
      isOpen={true}
      onSubmit={handleSubmit}
      onCancel={() => {
        if (isEditMode && !!form.formState.dirtyFields.roles) {
          setIsRemindModalActive(true);
        } else {
          closeModal();
        }
      }}
      submitButtonTestId={'submit-create-user-button'}
      cancelButtonText={i18n.t('cancel')}
      submitButtonText={i18n.t(isEditMode ? 'save' : 'create_user')}
      submitButtonType={ButtonColor.Primary}
      cancelButtonType={ButtonColor.Secondary}
      titleText={
        isEditMode
          ? `${i18n.t('edit')} ${i18n.t('user')}`
          : i18n.t('create_new_user')
      }
      containerClassName={styles.modalBodyWidth480}
      contentClassName={styles.scrollBar}
      isDisabled={isSubmitButtonDisabled}
      doNotCloseOnOutsideClick={true}
    >
      <Loading
        loading={isLoading}
        wrapperClassName={styles.loader}
        className={styles.absoluteContainer}
      />
      <div
        className={clsx(
          styles.columnContainer,
          !isEditMode && styles.marginTop20,
          isEditMode ? styles.rowGap46 : styles.rowGap36,
        )}
      >
        {isEditMode ? (
          <div>
            <div className={styles.label}>{i18n.t('name')}</div>
            <div
              data-test-id={'nameContainer'}
              className={clsx(styles.text, styles.marginTop10)}
            >
              {name}
            </div>
          </div>
        ) : (
          <Input
            testId={'name-input'}
            {...form.register('name')}
            className={styles.width432}
            containerClassName={styles.marginBottom24}
            topHelperText={i18n.t('name')}
          />
        )}
        {isEditMode ? (
          <div>
            <div className={styles.label}>{i18n.t('email')}</div>
            <div
              data-test-id={'emailContainer'}
              className={clsx(styles.text, styles.marginTop10)}
            >
              {email}
            </div>
          </div>
        ) : (
          <Input
            errorTestId={'input-error-div'}
            testId={'email-input'}
            {...form.register('email')}
            className={styles.width432}
            topHelperText={i18n.t('email')}
            errorText={form.formState.errors.email?.message}
          />
        )}
        <div className={styles.relative}>
          <div className={clsx(styles.label, styles.marginBottom4)}>
            {i18n.t('country')}
          </div>
          {isEditMode && isAbasUser ? (
            <div className={clsx(styles.text, styles.marginTop10)}>
              {
                countryOptions.find(
                  (country) => country.value === selectedUser?.countryCode,
                )?.label
              }
            </div>
          ) : (
            <div className={styles.multiSelectContainer}>
              <Autocomplete
                size="small"
                data-test-id={'country-select'}
                className={clsx(styles.selectBorder)}
                value={selectedCountry}
                options={countryOptions}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                onChange={(e, newValue) => {
                  if (newValue?.value) {
                    form.clearErrors('selectedCountry');
                    form.setValue('selectedCountry', newValue, {
                      shouldDirty: true,
                    });
                  }
                }}
                onInputChange={(e, value) => setCountryInputValue(value)}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    data-test-id={'country-input'}
                    className={clsx(
                      styles.placeholder,
                      styles.countryInput,
                      !!form.formState.errors.selectedCountry &&
                        styles.textFileErrorBorder,
                    )}
                    placeholder={i18n.t('select_country')}
                    error={!!form.formState.errors.selectedCountry}
                    helperText={form.formState.errors.selectedCountry?.message}
                  />
                )}
                disableCloseOnSelect={false}
                popupIcon={
                  <ArrowDown onMouseDown={(e) => e.preventDefault()} />
                }
                disablePortal={true}
                renderOption={(props, option) => (
                  <SelectOption
                    testId={'country-item'}
                    key={option.value}
                    option={option.label}
                    liProps={props}
                    selectedOptions={[selectedCountry.label]}
                    inputValue={countryInputValue}
                  />
                )}
              />
            </div>
          )}
        </div>
        <div className={styles.multiSelectContainer}>
          <div className={clsx(styles.label, styles.marginBottom4)}>
            {i18n.t('role')}
          </div>
          <Autocomplete
            multiple
            data-test-id={'role-select'}
            size="small"
            className={clsx(styles.selectBorder)}
            value={selectedRoles}
            options={allAvailableRoles}
            isOptionEqualToValue={(a, b) => a === b}
            onChange={(e, newValue) =>
              form.setValue('roles', newValue, {
                shouldDirty: true,
              })
            }
            onInputChange={(e, value) => setRolesInputValue(value)}
            getOptionLabel={(option) => option}
            renderInput={(params) => (
              <TextField
                {...params}
                className={clsx(styles.placeholder)}
                placeholder={i18n.t('select_role')}
              />
            )}
            disableCloseOnSelect={false}
            popupIcon={<ArrowDown onMouseDown={(e) => e.preventDefault()} />}
            disablePortal={true}
            renderOption={(props, option) => (
              <SelectOption
                testId={'role-item'}
                key={option}
                option={option}
                liProps={props}
                selectedOptions={selectedRoles}
                inputValue={rolesInputValue}
              />
            )}
          />
          <div
            className={clsx(
              styles.rowContainer,
              styles.columnGap5,
              styles.selectedErrorContainer,
            )}
          >
            {selectedRoles.map((role) => {
              return (
                <div
                  key={role}
                  data-test-id={'role-chip'}
                  className={clsx(
                    styles.rowContainer,
                    styles.columnGap5,
                    styles.selectedError,
                  )}
                >
                  <div>{role}</div>
                  <CloseIcon
                    className={styles.icon}
                    onClick={() => {
                      removeSelectedRole(role);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {isRemindModalActive && (
        <CustomModal
          isOpen={true}
          onSubmit={() => {
            setIsRemindModalActive(false);
            closeModal();
          }}
          onCancel={() => setIsRemindModalActive(false)}
          cancelButtonText={i18n.t('cancel')}
          submitButtonText={i18n.t('quit')}
          titleText={i18n.t('save_and_quit')}
          bodyText={i18n.t('unsaved_changes')}
          containerClassName={styles.modalWidth480}
          textClassName={styles.lineHeight24}
          submitButtonType={ButtonColor.Warning}
        />
      )}
    </CustomModal>
  );
};
